/*
 * Copyright (c) 2024 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { computed, inject, Signal } from '@angular/core';
import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap } from 'rxjs';
import { JsonApiResource } from '@irian-ro/abstract-resource';
import {
  SystemNamesEnum,
  WorkflowConfigDtoModel,
  WorkflowConfigMetadata,
  WorkflowExternalDefDtoModel,
  WorkflowExternalStepDefDtoModel,
  WorkflowListUiVo,
  WorkflowUiDefinitionModel,
} from '@well/iriro-frontend-libs/workflows/domain';
import { WorkflowConfigResourceService } from '@well/iriro-frontend-libs/workflows/resource-services';

interface WorkflowsState {
  workflowUiDefinitionModel: Record<string, WorkflowUiDefinitionModel>;
  workflowsConfiguration: WorkflowConfigDtoModel[];
}

const initialState: WorkflowsState = {
  workflowUiDefinitionModel: {},
  workflowsConfiguration: [],
};

const buildWorkflowDtoResource = (
  workflow: WorkflowUiDefinitionModel,
  workflowExternalDefDto: WorkflowExternalDefDtoModel | null,
  workflowExternalStepDefDtos: WorkflowExternalStepDefDtoModel[] | null
): JsonApiResource<WorkflowConfigDtoModel, WorkflowConfigMetadata> => {
  const data = new WorkflowConfigDtoModel();
  data.selectedNotifications = [];
  data.selectedSteps = [];
  data.workflowDefinitionUuid = null;
  data.workflowExternalDefDto = workflowExternalDefDto;
  data.description = null;
  data.label = null;
  data.workflowExternalStepDefDtos = workflowExternalStepDefDtos;

  const meta = new WorkflowConfigMetadata();
  meta.workflowName = workflow.name;
  meta.availableNotifications = [];
  meta.workflowDefaultDescription = workflow.defaultLabel;
  meta.availableSteps = [];
  meta.availableUiSteps = Object.keys(workflow.uiSteps).map((key) => workflow.uiSteps[key]);

  const resource = new JsonApiResource<WorkflowConfigDtoModel, WorkflowConfigMetadata>();
  resource.data = data;
  resource.meta = meta;

  return resource;
};

export const WorkflowsStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    workflowsUiDefinitionState: computed(() => store.workflowUiDefinitionModel()),

    workflowsDefinitionFilteredBySystemName: computed(() => {
      const state = store.workflowUiDefinitionModel();
      const list: WorkflowUiDefinitionModel[] = [];
      Object.keys(state).forEach((key) => {
        if (state[key].systemName === SystemNamesEnum.ANGULAR_FRONTEND) {
          list.push(state[key]);
        }
      });
      return list;
    }),

    workflowsDefinitionForFrontendState: computed(() => {
      const state = store.workflowUiDefinitionModel();
      const list: WorkflowListUiVo[] = [];
      Object.keys(state).forEach((key) => {
        if (state[key].systemName === SystemNamesEnum.ANGULAR_FRONTEND) {
          const item = state[key];
          const workflowListUiVo = new WorkflowListUiVo();
          workflowListUiVo.configuredForOwner = item.configuredForOwner;
          workflowListUiVo.configuredBy = item.configuredBy;
          workflowListUiVo.validTo = item.validTo;
          workflowListUiVo.validFrom = item.validFrom;
          workflowListUiVo.valid = item.configuredForOwner;
          workflowListUiVo.systemName = item.systemName;
          workflowListUiVo.definitionFile = item.definitionFile;
          workflowListUiVo.defaultLabel = item.defaultLabel;
          workflowListUiVo.name = item.name;
          workflowListUiVo.uuid = item.uuid;
          list.push(workflowListUiVo);
        }
      });
      return list;
    }),

    workflowsConfigurationsState: computed(() => store.workflowsConfiguration()),
  })),

  withMethods((store, configService = inject(WorkflowConfigResourceService)) => ({
    loadWorkflowsConfiguration: rxMethod<void>(
      pipe(
        switchMap(() => configService.getAllConfigs().pipe(map((res) => res.data))),
        tap((workflowsConfiguration: WorkflowConfigDtoModel[]) => patchState(store, { workflowsConfiguration }))
      )
    ),

    addFrontEndDefinitions(workflows: Record<string, WorkflowUiDefinitionModel>): void {
      patchState(store, { workflowUiDefinitionModel: workflows });
    },

    clearAllConfigurations(): void {
      patchState(store, { workflowsConfiguration: [] });
    },

    getWorkflowConfigurationByName(name: string): Signal<WorkflowConfigDtoModel | undefined> {
      return computed(() => {
        const workflows = store.workflowsConfiguration();
        return workflows.find((workflow) => workflow.workflowDefinitionName === name);
      });
    },

    getStepConfiguration(workflowName: string, stepId: string): Signal<any | undefined> {
      return computed(() => {
        const workflows = store.workflowsConfiguration();
        const workflow = workflows.find((w) => w.workflowDefinitionName === workflowName);
        return workflow?.selectedSteps?.find((step: any) => step.name === stepId);
      });
    },

    getWorkflowBySystemName(
      name: string
    ): Signal<JsonApiResource<WorkflowConfigDtoModel, WorkflowConfigMetadata> | null> {
      return computed(() => {
        const state = store.workflowUiDefinitionModel();
        if (state[name]) {
          return buildWorkflowDtoResource(state[name], null, null);
        }
        return null;
      });
    },

    getWorkflowBySystemNameForNewUIDefinitions(
      name: string
    ): Signal<JsonApiResource<WorkflowConfigDtoModel, WorkflowConfigMetadata> | null> {
      return computed(() => {
        const state = store.workflowUiDefinitionModel();
        if (state[name]) {
          return buildWorkflowDtoResource(
            state[name],
            WorkflowExternalDefDtoModel.createWorkflowExternalDefDtoModel(
              state[name].name,
              state[name].definitionFile,
              state[name].systemName
            ),
            []
          );
        }
        return null;
      });
    },
  }))
);
